import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supplierApi } from "../api/supplier-api";
import type { AuthSession, MeResponse } from "../types/supplier";
import { sessionStore } from "./session-store";

type AuthStage = "booting" | "anonymous" | "onboarding" | "pending" | "active";

interface AuthContextValue {
  stage: AuthStage;
  me: MeResponse | null;
  membershipStatus: string | null;
  acceptSession: (session: AuthSession) => Promise<void>;
  refreshProfile: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function membershipFrom(me: MeResponse) {
  return me.user?.supplierMemberships?.[0] ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [stage, setStage] = useState<AuthStage>("booting");
  const [me, setMe] = useState<MeResponse | null>(null);

  const applyMe = useCallback((nextMe: MeResponse) => {
    const membership = membershipFrom(nextMe);
    setMe(nextMe);
    setStage(
      !membership
        ? "onboarding"
        : membership.status === "active"
          ? "active"
          : "pending",
    );
  }, []);

  const refreshProfile = useCallback(async () => {
    applyMe(await supplierApi.me());
  }, [applyMe]);

  const acceptSession = useCallback(
    async (session: AuthSession) => {
      sessionStore.save(session);
      await refreshProfile();
      void import("../push/push")
        .then(({ registerWebPush }) => registerWebPush())
        .catch(() => undefined);
    },
    [refreshProfile],
  );

  const logout = useCallback(async () => {
    const session = sessionStore.load();
    sessionStore.clear();
    setMe(null);
    setStage("anonymous");
    if (session) {
      await supplierApi.logout(session.refreshToken).catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const stored = sessionStore.load();
    if (!stored) {
      setStage("anonymous");
      return;
    }

    supplierApi
      .refresh(stored.refreshToken)
      .then(acceptSession)
      .catch(() => {
        sessionStore.clear();
        setStage("anonymous");
      });
  }, [acceptSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      stage,
      me,
      membershipStatus: membershipFrom(me ?? {})?.status ?? null,
      acceptSession,
      refreshProfile,
      logout,
    }),
    [stage, me, acceptSession, refreshProfile, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider 안에서 useAuth를 사용해야 합니다.");
  return value;
}
